import { ActivityStatus } from "./enums";
import { normalizeItinerary } from "./general";
import { Option } from "./types";

export const toMinutes = (value: any, unit: string | undefined) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const num = Number(value);
  if (Number.isNaN(num)) return undefined;
  return unit === "hr" ? num * 60 : num;
};

export const fromMinutes = (mins: number | undefined | null) => {
  if (mins === undefined || mins === null) {
    return { value: undefined as number | undefined, unit: "min" as const };
  }
  return { value: mins, unit: "min" as const };
};

export function formatDurationByUnit(
  minutes?: number | string | null,
  unit?: string | null,
) {
  if (minutes === undefined || minutes === null || minutes === "") return null;

  const n = Number(minutes);
  if (Number.isNaN(n)) return null;

  if (unit === "hr") {
    const hours = Number((n / 60).toFixed(2));
    return `${hours}`;
  }

  return `${n}`;
}

/** Strips DB-only fields and cleans empty numeric strings before sending to the API. */
export const sanitizeSchedule = (schedule: any) => {
  if (!schedule) return schedule;

  const { id, createdAt, updatedAt, activity, exceptions, ageGroups, ...rest } =
    schedule;

  const cleanNumber = (val: any) =>
    val === "" || val === null ? undefined : val;

  return {
    ...rest,
    id: id || undefined,
    flatPrice: cleanNumber(rest.flatPrice),
    flatPriceType: rest.flatPriceType,
    minParticipants: cleanNumber(rest.minParticipants),
    maxParticipants: cleanNumber(rest.maxParticipants),
    endDate: cleanNumber(rest.endDate),

    exceptions: (exceptions || []).map((e: any) => {
      const { id: _id, schedule: _s, createdAt: _c, updatedAt: _u, ...r } = e;
      return r;
    }),

    ageGroups: (ageGroups || []).map((g: any) => {
      const { id: _id, schedule: _s, createdAt: _c, updatedAt: _u, ...r } = g;
      return {
        ...r,
        endAge: cleanNumber(r.endAge),
        price: cleanNumber(r.price),
      };
    }),
  };
};

export type GeoState = {
  pickupGeoMode: string;
  pickupPlaces: any[];
  pickupPolygon: any[];
  dropoffGeoMode: string;
  dropoffPlaces: any[];
  dropoffPolygon: any[];
  dropoffSameAsPickup: boolean;
};

export type GalleryState = { galleryItems: any[] };

/** Normalizes API / form payment methods to a clean string array (no empty entries). */
export const normalizePaymentMethods = (
  value: string | string[] | null | undefined,
): string[] => {
  if (value == null) return [];
  const items = Array.isArray(value)
    ? value
    : String(value)
        .split(",")
        .map((m) => m.trim());
  return items.filter(Boolean);
};

/**
 * Pure function — no component state closures. Same inputs always produce
 * the same FormData shape, which makes it independently testable.
 */
export const buildActivityFormData = (
  data: any,
  geo: GeoState,
  gallery: GalleryState,
  buildGeoData: (mode: string, places: any[], polygon: any[]) => any,
  statusOverride?: string,
): FormData => {
  const formData = new FormData();

  formData.append("title", data.title || "");
  formData.append(
    "status",
    statusOverride || data.status || ActivityStatus.DRAFT,
  );
  formData.append("placeId", data.placeId || "");
  formData.append("partnerCommission", String(data.partnerCommission ?? 0));
  formData.append(
    "paymentMethods",
    normalizePaymentMethods(data.paymentMethods).join(","),
  );
  formData.append("translations", JSON.stringify(data.translations || []));

  const cityId = data.cityId?.id ?? data.cityId;
  if (cityId) formData.append("cityId", String(cityId));

  formData.append("isActive", String(data.isActive ?? true));

  if (data.itineraries?.length > 0) {
    formData.append("itineraries", JSON.stringify(data.itineraries));
  }

  if (data.pickupDropoff) {
    const isPickupAvailable = !!data.pickupDropoff.isPickupAvailable;
    const isDropoffAvailable = !!data.pickupDropoff.isDropoffAvailable;

    const finalPickupGeojson = buildGeoData(
      geo.pickupGeoMode,
      geo.pickupPlaces,
      geo.pickupPolygon,
    );

    const finalPickupDropoff: any = {
      isPickupAvailable,
      isDropoffAvailable,
      pickupType: isPickupAvailable ? geo.pickupGeoMode : null,
      pickupDescription: data.pickupDropoff.pickupDescription || null,
      pickupGeojson: isPickupAvailable ? finalPickupGeojson : null,
      transportation: data.pickupDropoff.transportation || null,
      // reset dropoff fields by default, then override below
      dropoffType: null,
      dropoffDescription: null,
      dropoffGeojson: null,
    };

    if (isDropoffAvailable && geo.dropoffSameAsPickup) {
      finalPickupDropoff.dropoffType = geo.pickupGeoMode;
      finalPickupDropoff.dropoffDescription =
        data.pickupDropoff.pickupDescription || null;
      finalPickupDropoff.dropoffGeojson = finalPickupGeojson;
    } else if (isDropoffAvailable) {
      finalPickupDropoff.dropoffType = geo.dropoffGeoMode;
      finalPickupDropoff.dropoffDescription =
        data.pickupDropoff.dropoffDescription || null;
      finalPickupDropoff.dropoffGeojson = buildGeoData(
        geo.dropoffGeoMode,
        geo.dropoffPlaces,
        geo.dropoffPolygon,
      );
    }

    formData.append("pickupDropoff", JSON.stringify(finalPickupDropoff));
  }

  if (data.schedules?.length) {
    formData.append(
      "schedules",
      JSON.stringify(data.schedules.map(sanitizeSchedule)),
    );
  }

  if (data.options?.length) {
    const optionsPayload = data.options.map((opt: Option) => {
      const {
        scheduleMode,
        scheduleId,
        schedule,
        durationUnit,
        freeCancelThresholdUnit,
        bookingThresholdUnit,
        ...rest
      } = opt;

      const base = {
        ...rest,
        scheduleMode,
        durationMinutes: toMinutes(opt.durationMinutes, durationUnit),
        freeCancelThreshold: toMinutes(
          opt.freeCancelThreshold,
          freeCancelThresholdUnit,
        ),
        bookingThreshold: toMinutes(opt.bookingThreshold, bookingThresholdUnit),
        durationUnit,
        freeCancelThresholdUnit,
        bookingThresholdUnit,
      };

      if (opt.scheduleMode === "existing" && scheduleId) {
        return { ...base, scheduleId };
      }
      if (opt.scheduleMode === "custom" && schedule) {
        return { ...base, schedule: sanitizeSchedule(schedule) };
      }

      return base;
    });
    formData.append("options", JSON.stringify(optionsPayload));
  }

  const galleryMetadata = gallery.galleryItems.map((item, index) => ({
    order: index,
    type: item.type,
    isExisting: item.isExisting,
    backendId: item.isExisting ? item.id.replace("existing-", "") : undefined,
  }));
  formData.append("galleries", JSON.stringify(galleryMetadata));

  gallery.galleryItems.forEach((item) => {
    if (!item.isExisting && item.file)
      formData.append("galleryFiles", item.file);
  });

  return formData;
};

/** Single source of truth for mapping a fetched Activity into RHF form values. */
export const mapResToFormValues = (res: any) => ({
  title: res.title,
  status: res.status,
  placeId: res.place?.id || res.placeId || "",
  isActive: res.isActive,
  cityId: res.city,
  partnerCommission: Number(res.partnerCommission ?? 0),
  paymentMethods: normalizePaymentMethods(res.paymentMethods),
  translations: res.translations?.length
    ? res.translations
    : [{ lang: "fr", title: "" }],
  itineraries: (res.itineraries || []).map((item: any, index: number) =>
    normalizeItinerary(item, index),
  ),
  pickupDropoff: res.pickupDropoff || {
    isPickupAvailable: false,
    isDropoffAvailable: false,
    pickupType: "",
    dropoffType: "",
    pickupGeojson: null,
    dropoffGeojson: null,
  },
  schedules: res.schedules || [],
  options: (res.options || []).map((o: Option) => {
    const duration = formatDurationByUnit(o.durationMinutes, o.durationUnit);
    const freeCancel = formatDurationByUnit(
      o.freeCancelThreshold,
      o.freeCancelThresholdUnit,
    );
    const booking = formatDurationByUnit(
      o.bookingThreshold,
      o.bookingThresholdUnit,
    );

    return {
      id: o.id,
      title: o.title,
      description: o.description,
      durationMinutes: duration,
      durationUnit: o.durationUnit,
      freeCancelThreshold: freeCancel,
      freeCancelThresholdUnit: o.freeCancelThresholdUnit,
      bookingThreshold: booking,
      bookingThresholdUnit: o.bookingThresholdUnit,
      meta: o.meta || {},
      scheduleMode: o.scheduleMode ?? (o.schedule ? "custom" : "default"),
      scheduleId: o.scheduleMode === "existing" ? o.schedule?.id : undefined,
      schedule: o.scheduleMode === "custom" ? o.schedule : undefined,
    };
  }),
  gallery: res.gallery || [],
});

export const mapGalleryResponse = (res: any) => {
  const galleryData = res?.galleries || [];
  if (!Array.isArray(galleryData)) return [];
  return galleryData
    .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
    .map((g: any) => ({
      id: `existing-${g.id}`,
      url: g.url,
      type: g.type === "VIDEO" ? "VIDEO" : "IMAGE",
      isExisting: true,
    }));
};

export const getApiErrorMessage = (error: any): string | undefined =>
  error?.response?.data?.message || error?.message;
